import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDemo } from '../contexts/DemoContext';

import { ProductCard } from './ProductCard';

import { AnimateSpin } from '@/components/AnimateSpin';
import { Badge } from '@/components/ui/Badge';
import { useListOriginalCategories } from '@/hooks/useListOriginalCategories';
import { useRecommendationProductsByCategory } from '@/hooks/useRecommendationProductsByCategory';

type Category = {
  category: string;
  categoryId: string;
};

function ProductCategory({ category }: { category: Category }) {
  const { recommendation, isLoading } = useRecommendationProductsByCategory(
    category.categoryId,
  );

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {isLoading && <AnimateSpin />}

      {!isLoading &&
        recommendation?.products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
    </div>
  );
}

export function CatalogPage() {
  const [selectedCategory, setSelectedCategory] = useState<null | Category>(
    null,
  );

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Demo.components.CatalogPage',
  });

  const { categories } = useListOriginalCategories();

  const { setNavigationHistory } = useDemo();

  function handleSelectCategory(category: Category) {
    setSelectedCategory(category);
  }

  useEffect(() => {
    if (categories && !selectedCategory) {
      setSelectedCategory({
        category: categories[0].category,
        categoryId: categories[0].categoryId,
      });
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      setNavigationHistory((value) => [
        ...value,
        `{ categoryView: '${selectedCategory.category}' }`,
      ]);
    }
  }, [selectedCategory, setNavigationHistory]);

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-medium text-foreground md:text-4xl">
            {t('title')} ({selectedCategory?.category.trim()})
          </h1>

          <p className="text-muted-foreground">{t('description')}</p>
        </div>

        <div className="flex h-[40px] max-w-[1400px] gap-2 overflow-scroll">
          {categories?.map((category) => (
            <Badge
              key={category.categoryId}
              variant={
                selectedCategory?.categoryId === category.categoryId
                  ? 'secondary'
                  : 'outline'
              }
              className="h-[30px] cursor-pointer text-center"
              onClick={() => handleSelectCategory(category)}
            >
              <span className="min-w-[100px] max-w-[300px] truncate text-center">
                {category.category}
              </span>
            </Badge>
          ))}
        </div>

        {selectedCategory && <ProductCategory category={selectedCategory} />}
        {/* Products Grid */}
      </div>
    </main>
  );
}
